import { Flame, Beef, Wheat, Droplet } from 'lucide-react';

/**
 * Percentage of a single macro's target remaining, or null when there's no
 * real, positive target to divide by. Dividing by a missing/zero target
 * previously rendered "Infinity% left" or "NaN% left" (see AGENTS.md
 * pre-launch audit, finding PROG-3) — null hides the percentage line
 * entirely instead of showing a broken-looking number.
 */
export function computeMacroPercentRemaining(remainingValue, targetValue) {
  if (!(targetValue > 0)) return null;
  return Math.round((remainingValue / targetValue) * 100);
}

/**
 * Sticky header showing remaining macros for the day.
 * Updates live as meals are selected.
 *
 * Props:
 * - remaining: { calories, protein, carbs, fat }
 * - target: { calories, protein, carbs, fat } (optional, for showing percentages)
 * - compact: boolean - show compact version
 * - dayWithinRange: boolean - the AUTHORITATIVE saved-day state is Within
 *   Range. `remaining` is the exact-target remainder, while Within Range uses
 *   the goal's tolerance bands: a day can be Within Range with 0 left (or a
 *   little over an exact target). Then an exhausted value is success-compatible
 *   (accent), never an error red. Off-range days keep the warning colors.
 */
export function remainingValueColor(pct, value, dayWithinRange) {
  if (value <= 0) return dayWithinRange ? 'text-accent' : 'text-red-500';
  if (pct !== null) {
    if (pct <= 10) return 'text-amber-500';
    if (pct <= 25) return 'text-yellow-500';
  }
  return 'text-foreground';
}

function RemainingMacrosHeader({ remaining, target, compact = false, label = 'Remaining', dayWithinRange = false }) {
  if (!remaining) return null;

  var calPct = target ? computeMacroPercentRemaining(remaining.calories, target.calories) : null;
  var protPct = target ? computeMacroPercentRemaining(remaining.protein, target.protein) : null;
  var carbPct = target ? computeMacroPercentRemaining(remaining.carbs, target.carbs) : null;
  var fatPct = target ? computeMacroPercentRemaining(remaining.fat, target.fat) : null;
  
  // Determine color based on remaining percentage and the saved-day state
  function getColor(pct, value) {
    return remainingValueColor(pct, value, dayWithinRange);
  }
  
  if (compact) {
    return (
      <div 
        className="flex items-center gap-4 text-sm"
        data-testid="remaining-macros-compact"
      >
        <span className="text-muted-foreground">{label}:</span>
        <span className={'font-mono font-semibold ' + getColor(calPct, remaining.calories)}>
          {Math.round(remaining.calories)} cal
        </span>
        <span className="text-muted-foreground">•</span>
        <span className={'font-mono ' + getColor(protPct, remaining.protein)}>
          {Math.round(remaining.protein)}g P
        </span>
        <span className={'font-mono ' + getColor(carbPct, remaining.carbs)}>
          {Math.round(remaining.carbs)}g C
        </span>
        <span className={'font-mono ' + getColor(fatPct, remaining.fat)}>
          {Math.round(remaining.fat)}g F
        </span>
      </div>
    );
  }
  
  return (
    <div 
      className="bg-secondary/30 border-b border-border/20 px-4 py-3"
      data-testid="remaining-macros-header"
    >
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm text-muted-foreground font-medium">Remaining for today</span>
      </div>
      
      <div className="grid grid-cols-4 gap-2">
        {/* Calories */}
        <div className="text-center">
          <div className="flex items-center justify-center gap-1 mb-0.5">
            <Flame size={12} className="text-orange-500" />
            <span className="text-xs text-muted-foreground">Cal</span>
          </div>
          <p className={'font-mono font-bold text-lg ' + getColor(calPct, remaining.calories)}>
            {Math.round(remaining.calories)}
          </p>
          {calPct !== null && (
            <p className="text-[10px] text-muted-foreground">{calPct}% left</p>
          )}
        </div>
        
        {/* Protein */}
        <div className="text-center">
          <div className="flex items-center justify-center gap-1 mb-0.5">
            <Beef size={12} className="text-red-500" />
            <span className="text-xs text-muted-foreground">Protein</span>
          </div>
          <p className={'font-mono font-bold text-lg ' + getColor(protPct, remaining.protein)}>
            {Math.round(remaining.protein)}g
          </p>
          {protPct !== null && (
            <p className="text-[10px] text-muted-foreground">{protPct}% left</p>
          )}
        </div>
        
        {/* Carbs */}
        <div className="text-center">
          <div className="flex items-center justify-center gap-1 mb-0.5">
            <Wheat size={12} className="text-amber-500" />
            <span className="text-xs text-muted-foreground">Carbs</span>
          </div>
          <p className={'font-mono font-bold text-lg ' + getColor(carbPct, remaining.carbs)}>
            {Math.round(remaining.carbs)}g
          </p>
          {carbPct !== null && (
            <p className="text-[10px] text-muted-foreground">{carbPct}% left</p>
          )}
        </div>
        
        {/* Fat */}
        <div className="text-center">
          <div className="flex items-center justify-center gap-1 mb-0.5">
            <Droplet size={12} className="text-blue-500" />
            <span className="text-xs text-muted-foreground">Fat</span>
          </div>
          <p className={'font-mono font-bold text-lg ' + getColor(fatPct, remaining.fat)}>
            {Math.round(remaining.fat)}g
          </p>
          {fatPct !== null && (
            <p className="text-[10px] text-muted-foreground">{fatPct}% left</p>
          )}
        </div>
      </div>
    </div>
  );
}

export default RemainingMacrosHeader;
